import { ComponentProps } from 'react'

import { Icon } from '@clientbase/clientbase-library'
import { Box, Checkbox, Stack, Typography } from '@mui/material'

import { statusChipBase } from 'components/Status/Status.styles'

type SetupAddonProps = {
  amount: number
  multiplier: number
  selected: boolean
  onToggle: (selected: boolean) => void
}

const formatMoney = (value?: number | null) =>
  (value ?? 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })

/** O que o time faz na implantação (SC-11421). */
const SETUP_INCLUDES: {
  icon: ComponentProps<typeof Icon>['icon']
  text: string
}[] = [
  {
    icon: 'uploadFile',
    text: 'Importação dos seus clientes, cobranças e assinaturas, de planilha ou de outro sistema'
  },
  {
    icon: 'autoAwesome',
    text: 'Normalização dos dados: CPF/CNPJ, e-mails, telefones e endereços conferidos'
  },
  {
    icon: 'settings',
    text: 'Configuração da conta: régua de cobrança, notas fiscais e meios de pagamento'
  },
  {
    icon: 'supervisorAccount',
    text: 'Treinamento do seu time com um especialista da Base de Clientes'
  }
]

// Add-on da assinatura nova: implantação feita pelo nosso time, pagamento único
// cobrado junto com a primeira cobrança do plano.
const SetupAddon = ({
  amount,
  multiplier,
  selected,
  onToggle
}: SetupAddonProps) => (
  <Box
    component="label"
    htmlFor="saas-setup-addon"
    sx={{
      display: 'block',
      cursor: 'pointer',
      padding: '16px',
      borderRadius: '8px',
      border: '1px solid',
      borderColor: selected ? 'primary.main' : '#E3E6E3',
      boxShadow: selected ? '0 0 0 1px #01F7C2 inset' : 'none',
      backgroundColor: selected ? '#E6FDEA' : '#FFFFFF',
      transition: 'border-color 0.15s ease, background-color 0.15s ease',
      '&:hover': { borderColor: selected ? 'primary.main' : '#BEC0BE' },
      '&:has(input:focus-visible)': {
        outline: '2px solid #366AB2',
        outlineOffset: 2
      }
    }}
  >
    <Stack gap={1.5}>
      <Stack direction="row" alignItems="flex-start" gap={1}>
        <Checkbox
          id="saas-setup-addon"
          checked={selected}
          onChange={(event) => onToggle(event.target.checked)}
          sx={{ p: 0, mt: '1px' }}
          inputProps={{ 'aria-describedby': 'saas-setup-addon-detail' }}
        />
        <Stack gap={0.5} flex={1}>
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            gap={1}
            flexWrap="wrap"
          >
            <Typography variant="smBold" component="span" color="#231F20">
              Quero a implantação assistida
            </Typography>
            <Box
              component="span"
              sx={{
                ...statusChipBase,
                color: '#294266',
                backgroundColor: '#F7FBFF'
              }}
            >
              Opcional
            </Box>
          </Stack>
          <Typography
            id="saas-setup-addon-detail"
            variant="smLight"
            component="span"
            color="#646664"
          >
            Nosso time deixa sua conta pronta para cobrar, sem você perder tempo
            com planilha.
          </Typography>
          <Typography
            variant="mdBold"
            component="span"
            color="#231F20"
            mt={0.5}
          >
            + {formatMoney(amount)}{' '}
            <Typography variant="smLight" component="span" color="#646664">
              pagamento único ({multiplier}x a mensalidade)
            </Typography>
          </Typography>
        </Stack>
      </Stack>

      <Stack
        component="ul"
        gap={1}
        m={0}
        p={0}
        pl="34px"
        sx={{ listStyle: 'none' }}
      >
        {SETUP_INCLUDES.map((item) => (
          <Stack
            component="li"
            key={item.text}
            direction="row"
            gap={1}
            alignItems="flex-start"
          >
            <Icon icon={item.icon} size={2} colorSX="#3D892D" />
            <Typography variant="smLight" component="span" color="#3F403F">
              {item.text}
            </Typography>
          </Stack>
        ))}
      </Stack>
    </Stack>
  </Box>
)

export default SetupAddon
